import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, ArrowRight, Check, Plus } from "lucide-react";
import {
  listProductionScenarios, applyProductionScenario, listOrderItems, listProductTypes,
  displayOrderNumber,
} from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";

const todayISO = () => {
  const d = new Date(); d.setHours(0,0,0,0); return d.toISOString().slice(0,10);
};

/**
 * ApplyScenarioModal — shared apply UX. Two-step flow:
 *   1. Pick a scenario (only is_active=true offered)
 *   2. Assign per-stage schedule + optional overrides, uncheck stages to exclude
 * Calls POST /production-scenarios/{id}/apply and returns the created tasks via onApplied.
 */
export default function ApplyScenarioModal({
  orders = [], preselectedOrderId = null, preselectedOrderItemId = null,
  onClose, onApplied, onGoCreateScenario,
}) {
  const [step, setStep] = useState(1);           // 1 = pick scenario, 2 = configure stages
  const [scenarios, setScenarios] = useState(null); // null = loading
  const [selected, setSelected] = useState(null);
  const [orderId, setOrderId] = useState(preselectedOrderId || (orders[0]?.id || ""));
  const [scope, setScope] = useState(preselectedOrderItemId ? "item" : "order");
  const [orderItemId, setOrderItemId] = useState(preselectedOrderItemId || "");
  const [items, setItems] = useState([]);
  const [ptMap, setPtMap] = useState({});
  const [saving, setSaving] = useState(false);
  const [stageState, setStageState] = useState({}); // stage_id -> {include, date, time, duration_minutes}

  useEffect(() => {
    (async () => {
      try {
        const list = await listProductionScenarios({ is_active: true });
        setScenarios(list);
      } catch (e) { toast.error(formatApiError(e)); setScenarios([]); }
    })();
  }, []);

  useEffect(() => {
    if (!orderId) return;
    (async () => {
      try {
        const [its, pts] = await Promise.all([listOrderItems(orderId), listProductTypes(true)]);
        setItems(its);
        setPtMap(Object.fromEntries(pts.map(p => [p.id, p])));
      } catch (e) { /* silent */ }
    })();
  }, [orderId]);

  const pickScenario = (s) => {
    setSelected(s);
    const initial = {};
    for (const st of s.stages) {
      initial[st.id] = {
        include: true,
        date: todayISO(),
        time: "",
        duration_minutes: st.default_duration_minutes ? String(st.default_duration_minutes) : "",
      };
    }
    setStageState(initial);
    setStep(2);
  };

  const toggle = (sid) => setStageState(s => ({ ...s, [sid]: { ...s[sid], include: !s[sid].include } }));
  const patch = (sid, patchObj) => setStageState(s => ({ ...s, [sid]: { ...s[sid], ...patchObj } }));

  const doApply = async () => {
    if (!selected) return;
    if (!orderId) { toast.error("Выберите заказ"); return; }
    if (scope === "item" && !orderItemId) { toast.error("Выберите позицию заказа"); return; }
    const includedStages = selected.stages.filter(st => stageState[st.id]?.include);
    if (includedStages.length === 0) { toast.error("Выберите хотя бы один этап"); return; }
    const missing = includedStages.filter(st => !stageState[st.id]?.date);
    if (missing.length) { toast.error("Укажите дату для каждого выбранного этапа"); return; }
    const badDur = includedStages.find(st => {
      const v = stageState[st.id]?.duration_minutes;
      if (v === "" || v == null) return false;
      const n = Number(v);
      return !Number.isFinite(n) || n <= 0;
    });
    if (badDur) { toast.error("Длительность должна быть больше 0"); return; }

    const payload = {
      order_id: orderId,
      order_item_id: scope === "item" ? orderItemId : null,
      stages: includedStages.map(st => {
        const s = stageState[st.id];
        return {
          stage_id: st.id,
          scheduled_date: s.date,
          start_time: s.time ? `${s.time}:00` : null,
          duration_minutes: (s.duration_minutes !== "" && s.duration_minutes != null)
            ? Number(s.duration_minutes) : null,
        };
      }),
    };
    setSaving(true);
    try {
      const created = await applyProductionScenario(selected.id, payload);
      toast.success("Производственный план добавлен");
      onApplied?.(created);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const activeOrder = orders.find(o => o.id === orderId);

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
         style={{ background: "rgba(35,23,16,0.5)" }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
         data-testid="mo-apply-scenario-modal">
      <div className="w-full md:max-w-xl bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl p-5 md:p-6 max-h-[92vh] overflow-y-auto"
           style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)" }}>
        <div className="flex items-center justify-between mb-4">
          <div className="text-[19px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
            {step === 1 ? "Выберите сценарий" : "Настройте этапы"}
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full grid place-items-center"
                  style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  data-testid="mo-apply-scenario-close" aria-label="Закрыть">
            <X size={13} />
          </button>
        </div>

        {step === 1 && (
          <>
            {scenarios === null ? (
              <div className="text-[12.5px] py-6 text-center" style={{ color: "#8B6F4B" }}>Загружаем…</div>
            ) : scenarios.length === 0 ? (
              <div className="rounded-xl bg-white p-6 text-center" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                   data-testid="mo-apply-empty">
                <div className="text-[15.5px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                  У Вас пока нет производственных сценариев
                </div>
                <div className="text-[12px] mt-1" style={{ color: "#8B6F4B" }}>
                  Сохраните последовательность этапов, которую Вы часто используете.
                </div>
                <div className="mt-4 flex flex-wrap gap-2 justify-center">
                  <button onClick={() => { onClose?.(); onGoCreateScenario?.(); }}
                          className="h-9 px-4 rounded-xl text-[12.5px]"
                          style={{ background: "#4A3728", color: "#F7EFE0" }}
                          data-testid="mo-apply-go-create">
                    Создать сценарий
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2" data-testid="mo-apply-scenario-list">
                {scenarios.map(s => (
                  <button key={s.id} onClick={() => pickScenario(s)}
                          className="w-full text-left rounded-xl bg-white px-4 py-3 flex items-center gap-3 transition hover:bg-[#FDF7EC]"
                          style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                          data-testid="mo-apply-scenario-option">
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>{s.name}</div>
                      <div className="text-[11.5px] mt-0.5" style={{ color: "#8B6F4B" }}>
                        {s.stages.length} {s.stages.length === 1 ? "этап" : s.stages.length < 5 ? "этапа" : "этапов"}
                      </div>
                    </div>
                    <ArrowRight size={14} color="#8B6F4B" />
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {step === 2 && selected && (
          <div className="space-y-3" data-testid="mo-apply-configure">
            <div className="rounded-lg bg-white px-3 py-2.5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
              <div className="text-[10.5px] tracking-[0.14em] uppercase" style={{ color: "#8B6F4B" }}>Сценарий</div>
              <div className="text-[14px]" style={{ color: "#2A1D14" }}>{selected.name}</div>
            </div>

            {/* Order + scope */}
            {!preselectedOrderId && (
              <div>
                <div className="text-[10.5px] tracking-[0.14em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Заказ</div>
                <select value={orderId} onChange={(e) => { setOrderId(e.target.value); setOrderItemId(""); }}
                        className="w-full h-10 px-3 rounded-lg bg-white text-[13px] outline-none"
                        style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                        data-testid="mo-apply-order">
                  {orders.map(o => <option key={o.id} value={o.id}>№ {displayOrderNumber(o.order_number)} · {o.event_date}</option>)}
                </select>
              </div>
            )}
            {preselectedOrderId && activeOrder && (
              <div className="text-[11.5px]" style={{ color: "#8B6F4B" }}
                   data-testid="mo-apply-order-preselected">
                Для заказа № {displayOrderNumber(activeOrder.order_number)}
              </div>
            )}

            <div>
              <div className="text-[10.5px] tracking-[0.14em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Что делаем</div>
              <div className="flex gap-2">
                <button onClick={() => { setScope("order"); setOrderItemId(""); }}
                        className="h-9 px-3.5 rounded-xl text-[12.5px]"
                        style={{ background: scope === "order" ? "#4A3728" : "#F7F1E7",
                                 color: scope === "order" ? "#F7EFE0" : "#4A3728",
                                 boxShadow: scope === "order" ? "none" : "inset 0 0 0 1px #EFE4D0" }}
                        data-testid="mo-apply-scope-order">Весь заказ</button>
                <button onClick={() => setScope("item")}
                        className="h-9 px-3.5 rounded-xl text-[12.5px]"
                        style={{ background: scope === "item" ? "#4A3728" : "#F7F1E7",
                                 color: scope === "item" ? "#F7EFE0" : "#4A3728",
                                 boxShadow: scope === "item" ? "none" : "inset 0 0 0 1px #EFE4D0" }}
                        data-testid="mo-apply-scope-item">Конкретная позиция</button>
              </div>
              {scope === "item" && (
                <select value={orderItemId} onChange={(e) => setOrderItemId(e.target.value)}
                        className="mt-2 w-full h-10 px-3 rounded-lg bg-white text-[13px] outline-none"
                        style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                        data-testid="mo-apply-item">
                  <option value="">— выберите позицию —</option>
                  {items.map(it => (
                    <option key={it.id} value={it.id}>
                      {ptMap[it.product_type_id]?.name || "Позиция"}{it.quantity > 1 ? ` · ${it.quantity} шт.` : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Stages configuration */}
            <div>
              <div className="text-[10.5px] tracking-[0.14em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Этапы</div>
              <div className="space-y-2" data-testid="mo-apply-stages">
                {selected.stages.map(st => {
                  const s = stageState[st.id] || {};
                  return (
                    <div key={st.id} className="rounded-lg bg-white px-3 py-2.5"
                         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", opacity: s.include ? 1 : 0.55 }}
                         data-testid="mo-apply-stage-row">
                      <div className="flex items-start gap-2.5">
                        <button onClick={() => toggle(st.id)}
                                className="w-6 h-6 rounded grid place-items-center shrink-0 mt-0.5"
                                style={{ background: s.include ? "#4A3728" : "#F7F1E7",
                                         color: s.include ? "#F7EFE0" : "#8B6F4B",
                                         boxShadow: s.include ? "none" : "inset 0 0 0 1px #C7A574" }}
                                data-testid="mo-apply-stage-toggle" aria-label="Включить">
                          {s.include && <Check size={12} strokeWidth={2.4} />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13.5px]" style={{ color: "#2A1D14" }}
                               data-testid="mo-apply-stage-title">{st.title}</div>
                          <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                            <input type="date" value={s.date || ""} onChange={(e) => patch(st.id, { date: e.target.value })}
                                   disabled={!s.include}
                                   className="h-8 px-2 rounded-md bg-white text-[12px] outline-none disabled:opacity-50"
                                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                                   data-testid="mo-apply-stage-date" />
                            <input type="time" value={s.time || ""} onChange={(e) => patch(st.id, { time: e.target.value })}
                                   disabled={!s.include}
                                   placeholder="время"
                                   className="h-8 px-2 rounded-md bg-white text-[12px] outline-none disabled:opacity-50"
                                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                                   data-testid="mo-apply-stage-time" />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="pt-2 flex justify-end gap-2">
              <button onClick={() => setStep(1)}
                      className="h-10 px-4 rounded-xl text-[13px]"
                      style={{ background: "transparent", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-apply-back">
                Назад
              </button>
              <button onClick={doApply} disabled={saving}
                      className="h-10 px-5 rounded-xl text-[13px]"
                      style={{ background: "#4A3728", color: "#F7EFE0" }}
                      data-testid="mo-apply-submit">
                {saving ? "Применяем..." : "Применить"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
